// tenantInsightsPlanData.ts — Compute Tenant Ranking / Tenant Comparison in the
// browser, from the plan JSON files.
//
// Why: both actions used to have only two paths, and neither works outside the
// customer they were built for. The direct-SQL endpoint (/ai/tenant-insights)
// queries one hard-coded Snowflake table, and the LLM fallback needs a SQL
// datasource holding the same columns. A deployment running on sample plan data
// has neither, so both actions came up blank.
//
// The plan JSON files already hold everything needed, so this provider reads
// them: the plan list from `search.json`, then each plan's data file, through the
// configured field mapping. Scope is the *current plan type* — a lease plan and a
// corporate plan are not comparable, and it keeps the number of files small.

import { AnalyticsSchemaConfig } from "../../../types/analytics";
import { PlanInfo, SearchPlan, SearchPlanType, SpaceInfo } from "../../../types/plan";
import { fetchCadviewerJson, JsonFetchConfig } from "../../../utils/jsonFetch";
import { TableColumnType, TableData } from "../types/table";
import {
  fieldText,
  readOccupantMetrics,
  resolveAnalyticsSchema,
  resolveOccupantTab,
} from "./analyticsSchema";

/** Config keys this provider needs. */
export interface TenantInsightsConfig extends JsonFetchConfig {
  globalApplicationFilesLocation: string;
  globalApplicationSearch: string;
  propertyJSONFilesLocation: string;
  analyticsSchema?: AnalyticsSchemaConfig;
}

export interface TenantInsightsRequest {
  action: "ranking" | "comparison";
  tenantName: string;
  /** Required for "comparison": the property whose peers are compared. */
  propertyName?: string;
  /** Restrict the scan to this plan type (the loaded plan's type). */
  planType?: string;
}

export interface TenantInsightsResult {
  table: TableData;
  totalLocations: number;
  category: string | null;
  /** Plan data files actually read — for an honest "scanned N plans" message. */
  scannedFiles: number;
  /** Plan type the scan was limited to, when it was. */
  scannedPlanType?: string;
  /** Set when the scan hit `MAX_DATA_FILES`, so partial results are not silent. */
  truncatedAt?: number;
}

/**
 * Cap on plan data files read for one request. Plan files are big — a single
 * property in the public demo is ~1.9 MB — so this is a hard stop, not a
 * nicety. It applies *after* the search-index pre-filter below, which normally
 * brings the count down to a handful. When it does bite, `truncatedAt` says so
 * rather than quietly presenting a partial ranking as complete.
 */
const MAX_DATA_FILES = 40;

/** Files fetched concurrently. */
const FETCH_CONCURRENCY = 6;

/** Cache of plan data files for the session, keyed by resolved URL. */
const planDataCache = new Map<string, Promise<PlanInfo | null>>();

/** Cache of the search index, which is itself megabytes wide. */
const searchIndexCache = new Map<string, Promise<SearchPlanType[]>>();

/** Clear the caches (used by tests and after a datasource change). */
export function clearPlanDataCache(): void {
  planDataCache.clear();
  searchIndexCache.clear();
}

/**
 * Resolve a `dataFileName` from search.json to a URL. Mirrors the rewrite in
 * Cadviewer.Context.fetchPlanJson, where some deployments still list the old
 * "/v2/json/property/retail/" prefix.
 */
function planDataUrl(config: TenantInsightsConfig, dataFileName: string): string {
  return (config.propertyJSONFilesLocation + "" + dataFileName).replace(
    "/v2/json/property/retail/",
    "/json/property/"
  );
}

function loadPlanData(
  config: TenantInsightsConfig,
  dataFileName: string
): Promise<PlanInfo | null> {
  const url = planDataUrl(config, dataFileName);
  const cached = planDataCache.get(url);
  if (cached) return cached;

  const pending = fetchCadviewerJson<PlanInfo>(config, url).catch((error) => {
    // One unreadable file must not sink the whole ranking.
    console.warn(`[TenantInsights] could not read ${url}:`, error);
    return null;
  });
  planDataCache.set(url, pending);
  return pending;
}

/** Run `task` over `items` with a bounded number of concurrent calls. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += limit) {
    results.push(...(await Promise.all(items.slice(i, i + limit).map(task))));
  }
  return results;
}

/**
 * Narrow the plans to read, using the space index `search.json` already carries.
 *
 * This is what makes the provider usable on a real portfolio: the public demo
 * lists 152 plans of ~1.9 MB each, so scanning blindly would pull hundreds of
 * megabytes for one ranking. The index gives every plan's space headers, so a
 * ranking only opens the plans where the tenant actually appears (16 of 152 for
 * a typical chain) and a comparison only opens its own property.
 *
 * The index carries space headers, not the mapped occupant name, so a dataset
 * whose header differs from its tenant field would filter to nothing — hence the
 * fallback to the full (capped) list.
 */
function selectCandidatePlans(
  plans: SearchPlan[],
  filter: { tenantName?: string; propertyName?: string }
): SearchPlan[] {
  const property = filter.propertyName?.trim().toLowerCase();
  if (property) {
    const atProperty = plans.filter(
      (p) => (p.propertyName || "").trim().toLowerCase() === property
    );
    if (atProperty.length > 0) return atProperty;
  }

  const tenant = filter.tenantName?.trim().toUpperCase();
  if (tenant) {
    const withTenant = plans.filter((p) =>
      (p.spaces || []).some(
        (s) =>
          String(s.spaceHeader || "").toUpperCase().includes(tenant) ||
          String(s.spaceNumber || "").toUpperCase() === tenant
      )
    );
    if (withTenant.length > 0) return withTenant;
  }

  return plans;
}

/** One occupied space, with its property context and mapped figures. */
interface PortfolioSpace {
  propertyName: string;
  propertyNumber: string;
  planName: string;
  space: SpaceInfo;
  tenantName: string;
  category: string;
  leaseEnd: string;
  area: number;
  sales: number;
  salesPerArea: number;
  rentPerArea: number;
  occCostPct: number;
}

/**
 * Read every space of the given plan type, once per (property, space number).
 *
 * The de-duplication matters: several plans of one property routinely share a
 * single data file (the demo portfolio lists LP0/LP1/LP2 all pointing at the same
 * JSON), and counting them once per plan would multiply a tenant's locations.
 */
async function collectPortfolioSpaces(
  config: TenantInsightsConfig,
  planType: string | undefined,
  filter: { tenantName?: string; propertyName?: string }
): Promise<{
  spaces: PortfolioSpace[];
  scannedFiles: number;
  scannedPlanType?: string;
  truncatedAt?: number;
}> {
  const searchUrl =
    config.globalApplicationFilesLocation + "" + config.globalApplicationSearch;
  let searchPromise = searchIndexCache.get(searchUrl);
  if (!searchPromise) {
    searchPromise = fetchCadviewerJson<SearchPlanType[]>(config, searchUrl);
    searchIndexCache.set(searchUrl, searchPromise);
  }
  const searchData = await searchPromise;
  const planTypes = Array.isArray(searchData) ? searchData : [];

  const wanted = planType?.toLowerCase().trim();
  const matching = wanted
    ? planTypes.filter((t) => (t.planType || "").toLowerCase().trim() === wanted)
    : planTypes;
  // An unknown plan type must not silently produce an empty ranking.
  const selected = matching.length > 0 ? matching : planTypes;
  const scannedPlanType = matching.length > 0 ? planType : undefined;

  const allPlans: SearchPlan[] = selected.flatMap((t) => t.plans || []);
  const plans = selectCandidatePlans(allPlans, filter);

  // One entry per data file, keeping the properties that reference it.
  const byFile = new Map<string, SearchPlan[]>();
  for (const plan of plans) {
    if (!plan?.dataFileName) continue;
    const list = byFile.get(plan.dataFileName);
    if (list) list.push(plan);
    else byFile.set(plan.dataFileName, [plan]);
  }

  const files = Array.from(byFile.keys());
  const truncatedAt = files.length > MAX_DATA_FILES ? files.length : undefined;
  const scanned = files.slice(0, MAX_DATA_FILES);

  const schema = resolveAnalyticsSchema(config.analyticsSchema, scannedPlanType);
  const seen = new Set<string>();
  const spaces: PortfolioSpace[] = [];

  await mapWithConcurrency(scanned, FETCH_CONCURRENCY, async (dataFileName) => {
    const planInfo = await loadPlanData(config, dataFileName);
    if (!planInfo?.spaces) return;

    // Property identity comes from search.json, not from the data file: shared
    // demo files carry a placeholder propertyNumber and no property name.
    const references = byFile.get(dataFileName) || [];
    const properties = new Map<string, SearchPlan>();
    for (const ref of references) {
      if (!properties.has(ref.propertyName)) properties.set(ref.propertyName, ref);
    }

    for (const ref of Array.from(properties.values())) {
      for (const space of planInfo.spaces) {
        const key = `${ref.propertyName}|${space.spaceNumber}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const detail = resolveOccupantTab(space, schema, "current").detail;
        const tenantName = fieldText(detail, "tenantName", schema) || space.spaceHeader || "";
        if (!tenantName) continue;

        const metrics = readOccupantMetrics(detail, schema);
        spaces.push({
          propertyName: ref.propertyName,
          propertyNumber: ref.propertyNumber,
          planName: ref.planName,
          space,
          tenantName,
          category: fieldText(detail, "category", schema),
          leaseEnd: fieldText(detail, "leaseEnd", schema),
          area: metrics.area,
          sales: metrics.sales,
          salesPerArea: metrics.salesPerArea,
          rentPerArea: metrics.rentPerArea,
          occCostPct: metrics.occCostPct,
        });
      }
    }
  });

  return { spaces, scannedFiles: scanned.length, scannedPlanType, truncatedAt };
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Descending by sales per area, entries without sales last. */
function bySalesPerAreaDesc(a: PortfolioSpace, b: PortfolioSpace): number {
  return (b.salesPerArea || 0) - (a.salesPerArea || 0);
}

/**
 * Column labels and types shared with the direct-SQL endpoint's response, so a
 * deployment that switches provider sees the same table.
 */
const RANKING_LABELS: Record<string, string> = {
  rank: "Rank",
  property_name: "Property",
  space_number: "Space #",
  gla_sf: "GLA",
  sales_r12_usd: "Sales R12",
  sales_psf: "Sales PSF",
  occupancy_cost_percent: "Occ. Cost %",
  lease_end: "Lease End",
};

const COMPARISON_LABELS: Record<string, string> = {
  rank: "Rank",
  tenant: "Tenant",
  space_number: "Space #",
  gla_sf: "GLA",
  sales_r12_usd: "Sales R12",
  sales_psf: "Sales PSF",
  occupancy_cost_percent: "Occ. Cost %",
  rent_psf: "Rent PSF",
};

const SHARED_COLUMN_TYPES: Record<string, TableColumnType> = {
  rank: "number",
  gla_sf: "area",
  sales_r12_usd: "money",
  sales_psf: "money",
  rent_psf: "money",
  occupancy_cost_percent: "percentage",
  lease_end: "date",
};

/**
 * Compute Tenant Ranking or Tenant Comparison from the plan data files.
 *
 * Ranking lists every location of the tenant across the scanned plan type,
 * ranked by sales per area. Comparison ranks the tenant against peers of the
 * same category at one property, excluding those without sales — the same rule
 * the SQL endpoint applies, so the two providers agree.
 */
export async function computeTenantInsightsFromPlanData(
  config: TenantInsightsConfig,
  request: TenantInsightsRequest
): Promise<TenantInsightsResult> {
  const { spaces, scannedFiles, scannedPlanType, truncatedAt } =
    await collectPortfolioSpaces(config, request.planType, {
      tenantName: request.tenantName,
      // A comparison never leaves its property, so it opens that one plan only.
      propertyName: request.action === "comparison" ? request.propertyName : undefined,
    });

  const schema = resolveAnalyticsSchema(config.analyticsSchema, scannedPlanType);
  const target = request.tenantName.trim().toUpperCase();
  const isTarget = (name: string) => name.toUpperCase().includes(target);

  const areaUnit = schema.areaUnitLabel;
  const currency = schema.currencyLabel;
  const labelSuffix = (labels: Record<string, string>) => ({
    ...labels,
    gla_sf: `GLA (${areaUnit})`,
  });

  if (request.action === "ranking") {
    const matches = spaces.filter((s) => isTarget(s.tenantName)).sort(bySalesPerAreaDesc);

    const data = matches.map((s, index) => ({
      rank: index + 1,
      property_name: s.propertyName,
      space_number: s.space.spaceNumber,
      gla_sf: round2(s.area),
      sales_r12_usd: round2(s.sales),
      sales_psf: round2(s.salesPerArea),
      occupancy_cost_percent: round2(s.occCostPct),
      lease_end: s.leaseEnd || "—",
    }));

    return {
      table: {
        labels: labelSuffix(RANKING_LABELS),
        data,
        columnTypes: SHARED_COLUMN_TYPES,
        currency,
        areaUnit,
      },
      totalLocations: data.length,
      category: matches.find((s) => s.category)?.category || null,
      scannedFiles,
      scannedPlanType,
      truncatedAt,
    };
  }

  // Comparison: peers of the same category, at the same property.
  const propertyName = (request.propertyName || "").trim();
  const atProperty = spaces.filter(
    (s) => s.propertyName.trim().toLowerCase() === propertyName.toLowerCase()
  );
  const category = atProperty.find((s) => isTarget(s.tenantName) && s.category)?.category || "";

  const peers = atProperty
    .filter((s) => (category ? s.category === category : true))
    // Tenants without sales would distort the ranking and the average.
    .filter((s) => s.sales > 0 || s.salesPerArea > 0)
    .sort(bySalesPerAreaDesc);

  const data = peers.map((s, index) => ({
    rank: index + 1,
    // Mark the tenant under review, as the SQL endpoint does.
    tenant: isTarget(s.tenantName) ? `▶ ${s.tenantName}` : s.tenantName,
    space_number: s.space.spaceNumber,
    gla_sf: round2(s.area),
    sales_r12_usd: round2(s.sales),
    sales_psf: round2(s.salesPerArea),
    occupancy_cost_percent: round2(s.occCostPct),
    rent_psf: round2(s.rentPerArea),
  }));

  return {
    table: {
      labels: labelSuffix(COMPARISON_LABELS),
      data,
      columnTypes: SHARED_COLUMN_TYPES,
      currency,
      areaUnit,
    },
    totalLocations: data.length,
    category: category || null,
    scannedFiles,
    scannedPlanType,
    truncatedAt,
  };
}
